import type { Store } from "@/data/repositories";
import type { AccountSession } from "@/domain/entities";
import { ApiError, type Api, type ProviderId } from "./ApiClient";

/** Sign-in state of this device: the session lives in settings, the server keeps the account. */
export class AccountService {
  constructor(
    private readonly api: Api,
    private readonly store: Store,
  ) {}

  providers(): Promise<ProviderId[]> {
    return this.api.providers();
  }

  async session(): Promise<AccountSession | undefined> {
    return (await this.store.settings.get()).session;
  }

  startEmail(email: string, locale: string): Promise<void> {
    return this.api.emailStart(email, locale);
  }

  async verifyEmail(email: string, code: string): Promise<AccountSession> {
    return this.adopt(await this.api.emailVerify(email, code));
  }

  /** Finishes an OAuth login: the one-time code from the redirect becomes a session. */
  async completeHandoff(code: string): Promise<AccountSession> {
    return this.adopt(await this.api.handoff(code));
  }

  /** Leaves the diary on the device; only the session and archive bookkeeping go. */
  async signOut(): Promise<void> {
    const session = await this.session();
    if (session) await this.api.logout(session.token).catch(() => undefined);
    await this.forget();
  }

  async deleteAccount(): Promise<void> {
    const session = await this.session();
    if (session) await this.api.deleteAccount(session.token);
    await this.forget();
  }

  /** Runs a call with the current token; a 401 drops the local session and rethrows. */
  async withToken<T>(call: (token: string) => Promise<T>): Promise<T> {
    const session = await this.session();
    if (!session) throw new ApiError(401, "signed_out");
    try {
      return await call(session.token);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) await this.forget();
      throw error;
    }
  }

  private async adopt(session: AccountSession): Promise<AccountSession> {
    await this.store.settings.update({ session, lastArchive: undefined });
    return session;
  }

  private async forget(): Promise<void> {
    await this.store.settings.update({ session: undefined, lastArchive: undefined });
  }
}
